// ─────────────────────────────────────────────────────────────
// ClerkAuth — Clerk authentication singleton for the DAW
//
// Usage:
//   await ClerkAuth.init()          ← call once in main.ts
//   ClerkAuth.isSignedIn()          ← check before booting
//   await ClerkAuth.getToken()      ← use in API fetch headers
//   await ClerkAuth.signOut()       ← sign out from StudioMenu
//   ClerkAuth.onSessionChange(fn)   ← react to sign in / out
// ─────────────────────────────────────────────────────────────

import {CloudflareConfig} from "../cloudflare/CloudflareConfig"

// Shape of the Clerk instance we actually use — avoids namespace-as-type TS error
interface ClerkInstance {
    load(options?: Record<string, unknown>): Promise<void>
    user: unknown | null | undefined
    session: {getToken(): Promise<string | null>} | null | undefined
    signOut(): Promise<void>
    addListener(callback: (event: {session: unknown}) => void): void
    mountSignIn(node: HTMLElement, options?: Record<string, unknown>): void
    unmountSignIn(node: HTMLElement): void
}

let _clerk: ClerkInstance | null = null
const _listeners: Array<(signedIn: boolean) => void> = []

export namespace ClerkAuth {

    /** Initialize Clerk. Must be called before any other method. */
    export const init = async (): Promise<ClerkInstance> => {
        if (_clerk) return _clerk

        // Dynamic import avoids Vite star-export resolution issues
        const ClerkModule = await import("@clerk/clerk-js")
        const ClerkClass = ClerkModule.default ?? (ClerkModule as Record<string, unknown>)["Clerk"]
        _clerk = new (ClerkClass as unknown as new (key: string) => ClerkInstance)(CloudflareConfig.CLERK_PUBLISHABLE_KEY)
        await _clerk.load({
            // Keep the Clerk session alive across page refreshes
            standardBrowser: true,
        })

        // Notify listeners whenever the session changes
        _clerk.addListener(({session}: {session: unknown}) => {
            const signedIn = !!session
            _listeners.forEach(fn => fn(signedIn))
        })

        return _clerk
    }

    /** True if a user is currently signed in. */
    export const isSignedIn = (): boolean => !!_clerk?.user

    /**
     * Returns a short-lived Clerk JWT for use in Authorization headers.
     * Throws if the user is not signed in.
     */
    export const getToken = async (): Promise<string> => {
        if (!_clerk?.session) throw new Error("[ClerkAuth] Not signed in")
        const token = await _clerk.session.getToken()
        if (!token) throw new Error("[ClerkAuth] Could not retrieve session token")
        return token
    }

    /** Sign out the current user. */
    export const signOut = async (): Promise<void> => {
        await _clerk?.signOut()
    }

    /**
     * Register a callback that fires whenever the sign-in state changes.
     * Returns an unsubscribe function.
     */
    export const onSessionChange = (fn: (signedIn: boolean) => void): (() => void) => {
        _listeners.push(fn)
        return () => {
            const idx = _listeners.indexOf(fn)
            if (idx !== -1) _listeners.splice(idx, 1)
        }
    }

    /** Underlying Clerk instance — use sparingly. */
    export const getClerk = (): ClerkInstance | null => _clerk
}
